import { useError, useGetAiDiscoverabilityReports, useGetAssessmentTemplates, useDiscoverSubjects, useLoader, } from "@piiqtechnologies/ui-lib";
import React, { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import { useAuthContext } from "./AuthContext";
import { buildCommunityViewModels } from "@utils/communityViewModels";
const AiDiscoverabilityContext = createContext(undefined);
export const AiDiscoverabilityProvider = ({ children }) => {
    const { setError } = useError();
    const { setLoader } = useLoader();
    const { user } = useAuthContext();
    const [fetchReports, reportsResponse, loadingReports, errorLoadingReports] = useGetAiDiscoverabilityReports();
    const [fetchTemplates, templatesResponse, loadingTemplates, errorLoadingTemplates] = useGetAssessmentTemplates();
    const [fetchSubjects, subjectsResponse, loadingSubjects, errorLoadingSubjects] = useDiscoverSubjects();
    const fetchAll = useCallback(() => {
        if (user) {
            fetchReports();
            fetchTemplates({ limit: 3000 });
            fetchSubjects({ limit: 3000 });
        }
    }, [user, fetchReports, fetchTemplates, fetchSubjects]);
    useEffect(() => {
        fetchAll();
    }, [fetchAll]);
    const isLoading = loadingReports || loadingTemplates || loadingSubjects;
    const error = errorLoadingReports || errorLoadingTemplates || errorLoadingSubjects;
    useEffect(() => {
        setLoader(isLoading, "loadingAiDiscoverability");
        return () => setLoader(false, "loadingAiDiscoverability");
    }, [isLoading]);
    useEffect(() => {
        if (error) {
            setError(error === null || error === void 0 ? void 0 : error.message);
        }
    }, [error]);
    const subjects = useMemo(() => { var _a; return (_a = subjectsResponse === null || subjectsResponse === void 0 ? void 0 : subjectsResponse.subjects) !== null && _a !== void 0 ? _a : []; }, [subjectsResponse]);
    const communities = useMemo(() => buildCommunityViewModels({
        templates: templatesResponse !== null && templatesResponse !== void 0 ? templatesResponse : [],
        subjects,
        reports: reportsResponse !== null && reportsResponse !== void 0 ? reportsResponse : [],
    }), [templatesResponse, subjects, reportsResponse]);
    const activeTemplatesCount = useMemo(() => {
        return (templatesResponse !== null && templatesResponse !== void 0 ? templatesResponse : []).filter((t) => t.isActive).length;
    }, [templatesResponse]);
    const totalTemplatesCount = useMemo(() => {
        return (templatesResponse !== null && templatesResponse !== void 0 ? templatesResponse : []).length;
    }, [templatesResponse]);
    const value = useMemo(() => ({
        reportsData: reportsResponse || [],
        subjects,
        communities,
        isLoading,
        error,
        refetchReports: fetchReports,
        refetchAll: fetchAll,
        activeTemplatesCount,
        totalTemplatesCount,
    }), [
        reportsResponse,
        subjects,
        communities,
        isLoading,
        error,
        fetchReports,
        fetchAll,
        activeTemplatesCount,
        totalTemplatesCount,
    ]);
    return React.createElement(AiDiscoverabilityContext.Provider, { value: value }, children);
};
export const useAiDiscoverabilityContext = () => {
    const context = useContext(AiDiscoverabilityContext);
    if (context === undefined) {
        throw new Error("useAiDiscoverabilityContext must be used within an AiDiscoverabilityProvider");
    }
    return context;
};
